import { Logger, memone } from '@deqo/foundation';
import {
    EventSourceBase,
    ModelMutateCallback,
    ModelMutation,
    ModelOperation,
    ModelRepository,
    ModelSnapshot
} from '@deqo/podopolis';
import clone from 'rfdc/default';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { PodTransactionData } from '../../../api/endpoints/pods';
import { ObjectManagerBackend, ObjectManagerBackendStreams } from '../../../concepts/objects/ObjectManagerBackend';

const errLog = Logger.get('app.errors');

export interface ReplayStreams extends ObjectManagerBackendStreams {
    position: number;
}

export class ReplayController extends EventSourceBase<ReplayStreams> implements ObjectManagerBackend {

    public static load(namespace: string, snapshot: ModelSnapshot, transactions: PodTransactionData[]) {

        // Generate checkpoints every 50 transactions
        const base = clone(snapshot);
        const checkpoints = new Map<number, ModelSnapshot>();
        checkpoints.set(0, clone(snapshot));

        for (let i = 50; i < transactions.length; i += 50) {
            const block = transactions.slice(i - 50, i);
            for (const tx of block) {
                ModelOperation.execAll(base, tx.writes as any);
            }
            checkpoints.set(i, clone(base));
        }

        return new ReplayController(
            namespace,
            checkpoints,
            transactions
        );
    }

    private snapshot: ModelSnapshot;
    private mutation: ModelMutation;
    private position: number;
    private realizedPosition: number;

    constructor(
        public readonly namespace: string,
        private checkpoints: Map<number, ModelSnapshot>,
        private transactions: PodTransactionData[]) {
        super();
        this.position = 0;
        this.snapshot = checkpoints.get(0);
    }

    public getLength() {
        return this.transactions.length;
    }

    public getPosition() {
        return this.position;
    }

    public setPosition(value: number) {
        this.position = Math.min(value, this.getLength());
        this.updateOnNextFrame();
        this.emit('position', value);
    }

    public setPositionToViewElement(elmtId: string) {
        const { $cset, $cappend } = ModelOperation.codes;
        // Find the last update of elmtId that was cset or cappend
        const txl = this.transactions;
        for (let i = txl.length - 1; i >= 0; i--) {
            const tx = txl[i];
            for (const [op, a, b] of tx.writes) {
                if (op === $cset && a === elmtId) {
                    this.setPosition(i);
                    return;
                }
                if (op === $cappend && b === elmtId) {
                    this.setPosition(i);
                    return;
                }
            }
        }
    }

    public generateId(): string {
        return uuid();
    }

    public getModel(): ModelRepository {
        return this.getMemoizedRepository(this.snapshot);
    }

    public getMutation(): ModelMutation {
        return this.mutation;
    }

    public mutate(impl: ModelMutateCallback): void {
        try {
            const repo = this.getModel();
            const input = { emit: this.emit.bind(this), };
            this.mutation = new ModelMutation(repo, input);
            const data = this.mutation.perform(impl);
            ModelOperation.execAll(this.snapshot, data.applyBatch);
            repo.promoteStagingToPending();
            repo.promotePendingToConfirmed(repo.getVersion() + 1);
            this.emit('modelMutation', ['local', data]);
        }
        finally {
            this.mutation = null;
        }
    }

    public shouldAllowCreates(): boolean {
        return true;
    }

    public shouldAutoAllocate(): boolean {
        return true;
    }

    @memone()
    private getMemoizedRepository(snapshot: ModelSnapshot) {
        return new ModelRepository(snapshot);
    }

    private updateOnNextFrame() {
        requestAnimationFrame(() => {
            if (this.destroyed()) return;
            try {
                this.update();
            }
            catch (e) {
                errLog.error(e);
            }
        });
    }

    private update() {
        const from = this.realizedPosition;
        const to = this.position;
        if (from === to) return;
        // Determine the checkpoint we should update from
        const cp = to - (to % 50);
        // Get ids of all components before update
        const beforeIds = Object.keys(this.snapshot.instances);
        // Clone checkpoint base and apply transactions
        const base = clone(this.checkpoints.get(cp));
        const writes = this.transactions.slice(cp, to).flatMap(tx => tx.writes) as ModelOperation[];
        ModelOperation.execAll(base, writes);
        this.snapshot = base;
        // Get ids of all components after update
        const afterIds = Object.keys(this.snapshot.instances);
        // Emit update stream with union of all possible ids
        this.emit('modelMutation', ['remote', {
            applyBatch: writes,
            writeSet: new Set([...beforeIds, ...afterIds]),
        }]);

        this.realizedPosition = to;
    }

    // public next() {
    //     const tx = this.transactions[this.ticker];
    //     if (!tx) return;
    //     ModelOperation.execAll(this.snapshot, tx.writes as any);
    //     this.ticker++;
    //     this.streams.push('ticker', this.ticker);
    // }

    // public getRoot() {
    //     return this.objects.get('root', Root);
    // }

    // private async load() {

    //     this.streams.push('state', 'loading');

    //     const { podId } = this.session.get(BoardInfo);
    //     this.snapshot = await this.api.exec(v2.pods.snapshots(podId, 'initial'));
    //     this.transactions = await this.api.exec(v2.pods.transactions(podId));

    //     const model = ModelView.create(this.snapshot);
    //     const objects = new ReplayObjectManager(model, this.resolver);

    //     this.objects = objects;
    //     this.streams.push('state', 'ready');
    // }
}
